/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * HomePage.tsx — localized primary operator workspace state machine.
 * It selects the waiting, active-call, or after-call surface from shared store
 * state and supplies operator-facing status and wrap-up controls. Transport,
 * call assignment, and durable call-note persistence are outside this page.
 */

import { useConnectionState, usePresence, useCall } from '@/store';
import { CustomerHeader, WorkspaceTabs } from '@/features/workspace';
import { operatorStore } from '@/store/operatorStore';
import { useState } from 'react';
import { useTranslation } from '@/i18n';

export function HomePage() {
  const connectionState = useConnectionState();
  const presence = usePresence();
  const call = useCall();

  // Show active call workspace
  if (call.phase === 'offered' || call.phase === 'assigned' || call.phase === 'connected') {
    return (
      <div className="flex flex-col gap-4 min-h-0 h-full">
        <CustomerHeader />
        <div className="flex-1 min-h-0 flex flex-col">
          <WorkspaceTabs />
        </div>
      </div>
    );
  }

  // Show ACW screen after call ends
  if (call.phase === 'ended') {
    return <AfterCallWork />;
  }

  // Waiting screen (default state: idle)
  return <WaitingScreen connectionState={connectionState} presenceStatus={presence.status} />;
}

/* ------------------------------------------------------------------ */
/*  Waiting Screen                                                    */
/* ------------------------------------------------------------------ */

function WaitingScreen({
  connectionState,
  presenceStatus,
}: {
  connectionState: string;
  presenceStatus: string;
}) {
  const t = useTranslation();
  const isReady = connectionState === 'connected' && presenceStatus === 'available';
  const isConnecting = connectionState === 'connecting' || connectionState === 'reconnecting';

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-8 px-4">
      {/* Pulse indicator */}
      <div className="relative">
        <div
          className={`w-24 h-24 rounded-full flex items-center justify-center ${
            isReady
              ? 'bg-emerald-500/20 border-2 border-emerald-500/50'
              : isConnecting
                ? 'bg-amber-500/20 border-2 border-amber-500/50'
                : 'bg-slate-500/20 border-2 border-slate-500/50'
          }`}
        >
          {isReady && (
            <div className="absolute inset-0 rounded-full bg-emerald-500/10 animate-ping" />
          )}
          <div
            className={`w-12 h-12 rounded-full ${
              isReady ? 'bg-emerald-500' : isConnecting ? 'bg-amber-500' : 'bg-slate-500'
            }`}
          />
        </div>
      </div>

      {/* Status text */}
      <div className="text-center">
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-sm)',
          }}
        >
          {isReady ? t('home.ready') : isConnecting ? t('home.connecting') : t('home.disconnected')}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
          {isReady
            ? t('home.waiting_call')
            : isConnecting
              ? t('home.connecting_server')
              : t('home.check_connection')}
        </p>
      </div>

      {/* Shift stats */}
      {isReady && <ShiftStats />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shift Stats                                                       */
/* ------------------------------------------------------------------ */

function ShiftStats() {
  const t = useTranslation();
  return (
    <div
      className="glass-panel"
      style={{ padding: 'var(--space-lg)', width: '100%', maxWidth: 480 }}
    >
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-base)',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          marginBottom: 'var(--space-md)',
          textAlign: 'center',
        }}
      >
        {t('home.shift_stats')}
      </h3>
      <div className="grid grid-cols-3 gap-4 text-center">
        <StatItem label={t('home.calls')} value="0" />
        <StatItem label={t('home.average_time')} value="—" />
        <StatItem label={t('home.ai_resolved')} value="—" />
      </div>
    </div>
  );
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 'var(--text-xl)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-display)',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  After-Call Work (ACW)                                              */
/* ------------------------------------------------------------------ */

function AfterCallWork() {
  const t = useTranslation();
  const call = useCall();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState('');

  const durationStr = call.durationMs
    ? `${Math.floor(call.durationMs / 60000)}:${String(Math.floor((call.durationMs % 60000) / 1000)).padStart(2, '0')}`
    : '—';

  const tags = [
    'home.tag.appointment',
    'home.tag.consultation',
    'home.tag.complaint',
    'home.tag.callback',
    'home.tag.sale',
    'home.tag.support',
    'home.tag.information',
    'home.tag.escalation',
  ];

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleReady = () => {
    // Dismiss wrap-up and return to idle/waiting
    operatorStore.getState().dismissWrapUp();
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4">
      <div
        className="glass-panel"
        style={{ width: '100%', maxWidth: 560, padding: 'var(--space-2xl)' }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-xl)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-xs)',
            textAlign: 'center',
          }}
        >
          {t('home.call_ended')}
        </h2>
        <p
          style={{
            color: 'var(--text-muted)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center',
            marginBottom: 'var(--space-lg)',
          }}
        >
          {t('home.duration', { duration: durationStr })}
          {call.endReason && (
            <span style={{ marginLeft: 8, opacity: 0.6 }}>({call.endReason})</span>
          )}
        </p>

        {/* Tags / Category */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-sm)',
              fontWeight: 500,
            }}
          >
            {t('home.category')}
          </label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                className={selectedTags.has(tag) ? 'glass-toggle' : 'glass-toggle-compact'}
                onClick={() => toggleTag(tag)}
                style={
                  selectedTags.has(tag)
                    ? { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' }
                    : undefined
                }
              >
                {t(tag)}
              </button>
            ))}
          </div>
        </div>

        {/* Comment */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <label
            style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-xs)',
              fontWeight: 500,
            }}
          >
            {t('home.comment')}
          </label>
          <textarea
            placeholder={t('home.comment_placeholder')}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--space-sm) var(--space-md)',
              borderRadius: 'var(--radius-control)',
              border: '1px solid var(--glass-border)',
              background: 'var(--toggle-bg)',
              color: 'var(--text-primary)',
              fontSize: 'var(--text-sm)',
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'var(--font-body)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Actions */}
        <button
          className="glass-button"
          onClick={handleReady}
          style={{ width: '100%', justifyContent: 'center', fontWeight: 600 }}
        >
          {t('home.ready_next')}
        </button>
      </div>
    </div>
  );
}
